import { useCallback, useMemo, useState } from "react";

import { Flex, Pagination, Stack, Table } from "@mantine/core";

import useClientSidePagination from "@/billing/hooks/useClientSidePagination";
import { OpenStatementsRow } from "@/billing/types/open-statements";
import {
  DEFAULT_OPEN_STATEMENTS_TABLE_SORT_STATE,
  getNextOpenStatementsTableSortState,
  OpenStatementsSortColumnKey,
  OpenStatementsTableSortState,
  sortOpenStatementsTableRows,
} from "@/billing/utils/open-statements-table-sort";
import { Loading } from "@/components/Loading";
import ScrollableTable from "@/components/ScrollableTable";
import { TextWrapper } from "@/components/TextWrapper";

import BillingSortableTh from "../BillingSortableTh";

import {
  LOAD_ERROR_MESSAGE,
  NO_SEARCH_RESULTS_MESSAGE,
  OPEN_STATEMENTS_COLUMN_SPAN,
  OPEN_STATEMENTS_COLUMNS,
  OPEN_STATEMENTS_PAGE_SIZE,
  TABLE_HEADER_HEIGHT,
} from "./constants/open-statements";
import OpenStatementsEmptyState from "./OpenStatementsEmptyState";
import OpenStatementsTableRow from "./OpenStatementsTableRow";

const LOADING_HEIGHT = 96;

interface OpenStatementsTableProps {
  /** Already filtered by the search, so paging and page count follow the visible rows. */
  rows?: OpenStatementsRow[];
  isLoading?: boolean;
  /** True while payer names are being read, before a payer search can match anything. */
  isResolvingPayers?: boolean;
  /** True while a search is narrowing the rows, which changes what an empty table means. */
  isSearching?: boolean;
  error?: Error;
  refreshKey?: number;
  /** Whether a status filter is applied, so an empty table can say why it is empty. */
  isFiltered?: boolean;
}

export default function OpenStatementsTable({
  rows = [],
  isLoading = false,
  isResolvingPayers = false,
  isSearching = false,
  error,
  refreshKey = 0,
  isFiltered = false,
}: OpenStatementsTableProps) {
  const [sortState, setSortState] = useState<OpenStatementsTableSortState>(
    DEFAULT_OPEN_STATEMENTS_TABLE_SORT_STATE,
  );
  const [appliedRefreshKey, setAppliedRefreshKey] = useState(refreshKey);
  const [appliedRows, setAppliedRows] = useState(rows);

  // Sorting runs over every row, not just the visible page, so the order the user asked for holds
  // across the whole result set instead of shuffling rows within each page.
  const sortedRows = useMemo(() => sortOpenStatementsTableRows(rows, sortState), [rows, sortState]);

  const { pageRows, totalPages, displayedPage, setCurrentPage, resetToFirstPage } =
    useClientSidePagination(sortedRows, OPEN_STATEMENTS_PAGE_SIZE);

  // A refresh reloads the whole list, so the page the user was on no longer means anything. Reset
  // during render rather than in an effect, so the first paint after a refresh is already page 1.
  if (refreshKey !== appliedRefreshKey) {
    setAppliedRefreshKey(refreshKey);
    resetToFirstPage();
  }

  // A search that narrows the rows renumbers the pages under the user, so the page they were on no
  // longer holds the rows they were reading. Reset during render, like the refresh above, so the
  // first paint after a query change already shows page 1 of the matches.
  if (rows !== appliedRows) {
    setAppliedRows(rows);
    resetToFirstPage();
  }

  const handleSort = useCallback(
    (columnKey: OpenStatementsSortColumnKey) => {
      setSortState((current) => getNextOpenStatementsTableSortState(current, columnKey));
      // Re-sorting reorders every row, so the old page number no longer points at the rows the user
      // was looking at. Send them to the start of the new order.
      resetToFirstPage();
    },
    [resetToFirstPage],
  );

  return (
    <Stack gap="md">
      <ScrollableTable withRowBorders data-testid="open-statements-table">
        <Table.Thead bg="cream.2">
          <Table.Tr h={TABLE_HEADER_HEIGHT}>
            {OPEN_STATEMENTS_COLUMNS.map((column) => (
              <BillingSortableTh
                labelSize="md"
                key={column.key}
                w={column.width}
                ta={column.align}
                onSort={handleSort}
                label={column.label}
                sortState={sortState}
                columnKey={column.sort}
                testId={`open-statements-sort-${column.key}`}
              />
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {(isLoading || isResolvingPayers) && (
            <Table.Tr data-testid="open-statements-table-loading">
              <Table.Td colSpan={OPEN_STATEMENTS_COLUMN_SPAN}>
                <Loading height={LOADING_HEIGHT} />
              </Table.Td>
            </Table.Tr>
          )}
          {!isLoading && !isResolvingPayers && error && (
            <Table.Tr>
              <Table.Td colSpan={OPEN_STATEMENTS_COLUMN_SPAN}>
                <TextWrapper size="md" c="red.7" data-testid="open-statements-table-error">
                  {LOAD_ERROR_MESSAGE}
                </TextWrapper>
              </Table.Td>
            </Table.Tr>
          )}
          {!isLoading && !isResolvingPayers && !error && rows.length === 0 && (
            <OpenStatementsEmptyState
              message={isSearching ? NO_SEARCH_RESULTS_MESSAGE : undefined}
            />
          )}
          {!isLoading &&
            !isResolvingPayers &&
            !error &&
            pageRows.map((row) => <OpenStatementsTableRow row={row} key={row.id} />)}
        </Table.Tbody>
      </ScrollableTable>
      {!isLoading && !isResolvingPayers && !error && (
        <Flex justify="center">
          <Pagination
            hideWithOnePage
            total={totalPages}
            value={displayedPage}
            onChange={setCurrentPage}
            data-testid="open-statements-pagination"
          />
        </Flex>
      )}
    </Stack>
  );
}
